import { FieldValues } from "react-hook-form";
import styled from "@emotion/styled";

import {
  ExtendedGroupedStageOptions,
  GroupedStageOption,
} from "components/shared/stage/GroupedStageDropdownSelect";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectProps as SelectFieldProps,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "components/ui/select/Select";
import { ExtendedStageOption } from "../../../shared/stage/utils";
import { Stage } from "./Stage";

function renderStageOption({
  selectedOption,
  truncate,
}: {
  selectedOption: GroupedStageOption;
  truncate?: boolean;
}) {
  const labelAsString = selectedOption?.label?.toString() || "";

  return (
    <Stage
      label={labelAsString}
      color={selectedOption?.color}
      iconColor={selectedOption?.iconColor}
      iconName={selectedOption?.iconName}
      truncate={truncate}
    />
  );
}

export function StageSelect<TFieldValues extends FieldValues = FieldValues>(
  props: Omit<SelectFieldProps<TFieldValues>, "children"> & {
    options: ExtendedGroupedStageOptions[] | ExtendedStageOption[];
    truncate?: boolean;
  },
) {
  const { truncate = false, ...selectProps } = props;
  const isGrouped =
    Array.isArray(props.options) && props.options.length > 0 && "options" in props.options[0];

  return (
    <Select {...selectProps}>
      <StyledTrigger data-testid="stage-select-trigger">
        <SelectValue placeholder="Select stage" />
      </StyledTrigger>
      <StyledSelectContent sideOffset={4} aria-label="Stage options">
        {isGrouped
          ? (props.options as ExtendedGroupedStageOptions[]).map((group, index) => (
              <div key={group.value}>
                {index > 0 && <SelectSeparator />}
                <SelectGroup>
                  <SelectLabel>{group.label}</SelectLabel>
                  {group.options.map((option) => (
                    <StyledSelectItem key={option.value} value={option.value}>
                      {renderStageOption({ selectedOption: option, truncate })}
                    </StyledSelectItem>
                  ))}
                </SelectGroup>
              </div>
            ))
          : (props.options as ExtendedStageOption[]).map((option) => (
              <StyledSelectItem key={option.value} value={option.value}>
                {renderStageOption({ selectedOption: option, truncate })}
              </StyledSelectItem>
            ))}
      </StyledSelectContent>
    </Select>
  );
}

const StyledSelectContent = styled(SelectContent)({
  width: "auto",
  maxWidth: 320,
  minWidth: "var(--radix-select-trigger-width)",
  "& [data-radix-select-viewport]": {
    width: "auto",
    minWidth: "var(--radix-select-trigger-width)",
  },
});

const StyledSelectItem = styled(SelectItem)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px",
});

const StyledTrigger = styled(SelectTrigger)({
  padding: "4px 4px 4px 8px",
  borderRadius: 4,
});
